import { zodResolver } from '@hookform/resolvers/zod'
import {
  Clock,
  DollarSign,
  Flame,
  Globe,
  Layers,
  Link2,
  MessageSquare,
  Mic,
  Music2,
  Package,
  Sparkles,
  Star,
  TextCursorInput,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { Button } from '@/components/ui/Button'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { CDPConnectButton } from './CDPConnectButton'
import {
  useStartProductReview,
  type ProductReviewInput,
} from '@/api/endpoints/factories'
import { useTtsVoices } from '@/api/endpoints/media'
import { useIsAdmin } from '@/api/endpoints/me'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import { VoiceCarousel } from '../ViralShortsForm/VoiceCarousel'
import { toVoiceOption } from '../ViralShortsForm/voiceConstants'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

const FIELD_LABELS: Record<string, string> = {
  url: 'URL do produto',
  language: 'Idioma',
  tone: 'Tom',
  focus: 'Foco',
  duration: 'Duração',
  mix_mode: 'Mídia',
  music_mood: 'Trilha',
  custom_cta: 'CTA',
  subtitle_position: 'Legendas',
  tts_voice_pt: 'Narrador (PT)',
  tts_voice_en: 'Narrador (EN)',
}

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type ToneKey = 'enthusiastic' | 'neutral' | 'critical' | 'comedic'
const TONES: ReadonlyArray<SegmentedOption<ToneKey>> = [
  { value: 'enthusiastic', label: 'Entusiasta' },
  { value: 'neutral', label: 'Neutro' },
  { value: 'critical', label: 'Crítico' },
  { value: 'comedic', label: 'Cômico' },
]

type FocusKey = 'features' | 'benefits' | 'unboxing' | 'comparison'
const FOCUSES: ReadonlyArray<SegmentedOption<FocusKey>> = [
  { value: 'benefits', label: 'Benefícios' },
  { value: 'features', label: 'Specs' },
  { value: 'unboxing', label: 'Unboxing' },
  { value: 'comparison', label: 'Comparação' },
]

type DurationKey = '30' | '45' | '60' | '75'
const DURATIONS: ReadonlyArray<SegmentedOption<DurationKey>> = [
  { value: '30', label: '30s' },
  { value: '45', label: '45s' },
  { value: '60', label: '60s' },
  { value: '75', label: '75s' },
]

type MixModeKey = 'product_first' | 'mix' | 'pexels_only'
const MIX_MODES: ReadonlyArray<SegmentedOption<MixModeKey>> = [
  { value: 'product_first', label: 'Produto primeiro' },
  { value: 'mix', label: 'Misturar' },
  { value: 'pexels_only', label: 'Só B-roll' },
]

type MusicMoodKey = 'energetic' | 'neutral' | 'calm'
const MUSIC_MOODS: ReadonlyArray<SegmentedOption<MusicMoodKey>> = [
  { value: 'energetic', label: 'Energética' },
  { value: 'neutral', label: 'Neutra' },
  { value: 'calm', label: 'Calma' },
]

type SubtitleKey = 'top' | 'center' | 'bottom'
const SUBTITLE_POSITIONS: ReadonlyArray<SegmentedOption<SubtitleKey>> = [
  { value: 'top', label: 'Topo' },
  { value: 'center', label: 'Centro' },
  { value: 'bottom', label: 'Base' },
]

const schema = z.object({
  url: z.url('URL inválida — comece com http:// ou https://').min(1, 'Cole a URL do produto'),
  language: z.enum(['pt-br', 'en', 'both']),
  tone: z.enum(['enthusiastic', 'neutral', 'critical', 'comedic']),
  focus: z.enum(['features', 'benefits', 'unboxing', 'comparison']),
  duration: z.enum(['30', '45', '60', '75']),
  mix_mode: z.enum(['product_first', 'mix', 'pexels_only']),
  music_mood: z.enum(['energetic', 'neutral', 'calm']),
  subtitle_position: z.enum(['top', 'center', 'bottom']),
  support_texts: z.boolean(),
  mention_price: z.boolean(),
  mention_rating: z.boolean(),
  custom_cta: z.string().trim().max(60).optional(),
  tts_voice_pt: z.string().optional(),
  tts_voice_en: z.string().optional(),
  llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
})

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  url: '',
  language: 'pt-br',
  tone: 'enthusiastic',
  focus: 'benefits',
  duration: '45',
  mix_mode: 'product_first',
  music_mood: 'energetic',
  subtitle_position: 'bottom',
  support_texts: true,
  mention_price: true,
  mention_rating: true,
  custom_cta: '',
}

const SECONDS_TO_DURATION_KEY: Record<number, DurationKey> = {
  30: '30',
  45: '45',
  60: '60',
  75: '75',
}

/** Form snapshot → preset payload. ``url`` is intentionally omitted —
 * presets describe how to review a product, not which one. */
function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    language: values.language,
    tone: values.tone,
    focus: values.focus,
    duration: Number(values.duration),
    mix_mode: values.mix_mode,
    music_mood: values.music_mood,
    subtitle_position: values.subtitle_position,
    support_texts: values.support_texts,
    mention_price: values.mention_price,
    mention_rating: values.mention_rating,
  }
  if (values.custom_cta?.trim()) payload.custom_cta = values.custom_cta.trim()
  if (values.tts_voice_pt) payload.tts_voice_pt = values.tts_voice_pt
  if (values.tts_voice_en) payload.tts_voice_en = values.tts_voice_en
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  const seconds = (params.duration as number | undefined) ?? 45
  const v: FormValues = {
    ...FORM_DEFAULTS,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    tone: (params.tone as FormValues['tone'] | undefined) ?? FORM_DEFAULTS.tone,
    focus: (params.focus as FocusKey | undefined) ?? FORM_DEFAULTS.focus,
    duration: SECONDS_TO_DURATION_KEY[seconds] ?? FORM_DEFAULTS.duration,
    mix_mode: (params.mix_mode as MixModeKey | undefined) ?? FORM_DEFAULTS.mix_mode,
    music_mood: (params.music_mood as MusicMoodKey | undefined) ?? FORM_DEFAULTS.music_mood,
    subtitle_position:
      (params.subtitle_position as SubtitleKey | undefined) ?? FORM_DEFAULTS.subtitle_position,
    support_texts:
      (params.support_texts as boolean | undefined) ?? FORM_DEFAULTS.support_texts,
    mention_price:
      (params.mention_price as boolean | undefined) ?? FORM_DEFAULTS.mention_price,
    mention_rating:
      (params.mention_rating as boolean | undefined) ?? FORM_DEFAULTS.mention_rating,
    custom_cta: (params.custom_cta as string | undefined) ?? '',
  }
  if (params.tts_voice_pt) v.tts_voice_pt = params.tts_voice_pt as string
  if (params.tts_voice_en) v.tts_voice_en = params.tts_voice_en as string
  return v
}

export function ProductReviewForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''
  const isAdmin = useIsAdmin()

  const { data: voicesData, isPending: voicesPending } = useTtsVoices('', 'all')
  const ptVoices = (voicesData?.voices ?? [])
    .filter((v) => v.language === 'pt-br')
    .map(toVoiceOption)
  const enVoices = (voicesData?.voices ?? [])
    .filter((v) => v.language === 'en')
    .map(toVoiceOption)
  const voiceProvider = voicesData?.provider ?? ''

  const startMutation = useStartProductReview()

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      url: '',
      language: 'pt-br',
      tone: 'enthusiastic',
      focus: 'benefits',
      duration: '45',
      mix_mode: 'product_first',
      music_mood: 'energetic',
      subtitle_position: 'bottom',
      support_texts: true,
      mention_price: true,
      mention_rating: true,
      custom_cta: '',
      llm_provider: 'auto',
    },
  })

  const language = form.watch('language')
  const tone = form.watch('tone')
  const focus = form.watch('focus')
  const duration = form.watch('duration')
  const mixMode = form.watch('mix_mode')
  const musicMood = form.watch('music_mood')
  const subtitlePosition = form.watch('subtitle_position')
  const supportTexts = form.watch('support_texts')
  const mentionPrice = form.watch('mention_price')
  const mentionRating = form.watch('mention_rating')
  const voicePt = form.watch('tts_voice_pt')
  const voiceEn = form.watch('tts_voice_en')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload: ProductReviewInput = {
      url: values.url.trim(),
      channel: channel || null,
      language: values.language,
      tone: values.tone,
      focus: values.focus,
      duration: Number(values.duration),
      mix_mode: values.mix_mode,
      music_mood: values.music_mood,
      subtitle_position: values.subtitle_position,
      support_texts: values.support_texts,
      mention_price: values.mention_price,
      mention_rating: values.mention_rating,
      custom_cta: (values.custom_cta ?? '').trim(),
      force: false,
      tts_voice_pt: values.tts_voice_pt || null,
      tts_voice_en: values.tts_voice_en || null,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Package aria-hidden /> Review de produto
          </div>
          <h1 className={styles.title}>Cole o link e a gente roteiriza.</h1>
          <p className={styles.subtitle}>
            URL de Amazon / Shopee / loja própria. O scraper puxa título, preço, fotos e reviews; o
            roteiro nasce em PT-BR + EN se você escolher “Ambos”.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="product_review"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                // Preserve whichever URL the user already pasted — presets
                // don't pin a product URL.
                const currentUrl = form.getValues('url')
                form.reset({ ...values, url: currentUrl })
              }}
            />
          ) : null}

          <div className={styles.rows}>
            {/* Status + launch trigger for the managed Chromium. Sits above
                the URL row because Shopee scrapes silently fail when the
                browser isn't up — the user needs to see the connection
                state before pasting a URL. Só o admin: o navegador é do
                operador, com a sessão dele. Os outros colam o HTML. */}
            {isAdmin ? (
              <CDPConnectButton />
            ) : (
              <p className={styles.subtitle}>
                Para Shopee, cole o HTML da página ou use o bookmarklet: o navegador de raspagem é só do
                admin.
              </p>
            )}

            <Row icon={<Link2 />} label="URL do produto" htmlFor="review-url">
              <input
                id="review-url"
                type="url"
                inputMode="url"
                autoComplete="url"
                placeholder="https://www.amazon.com.br/..."
                className={cn(
                  styles.textarea,
                  form.formState.errors.url && styles.textareaError,
                )}
                {...form.register('url')}
              />
              {form.formState.errors.url ? (
                <span className={styles.errorText}>{form.formState.errors.url.message}</span>
              ) : null}
            </Row>

            <Row icon={<MessageSquare />} label="Tom">
              <Segmented<ToneKey>
                options={TONES}
                value={tone}
                onValueChange={(v) => {
                  form.setValue('tone', v)
                }}
                aria-label="Tom"
              />
            </Row>

            <Row icon={<Flame />} label="Foco">
              <Segmented<FocusKey>
                options={FOCUSES}
                value={focus}
                onValueChange={(v) => {
                  form.setValue('focus', v)
                }}
                aria-label="Foco"
              />
            </Row>

            <Row icon={<Clock />} label="Duração">
              <Segmented<DurationKey>
                options={DURATIONS}
                value={duration}
                onValueChange={(v) => {
                  form.setValue('duration', v)
                }}
                aria-label="Duração"
              />
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Globe />} label="Idioma">
                  <Segmented<'pt-br' | 'en' | 'both'>
                    options={LANGUAGES}
                    value={language}
                    onValueChange={(v) => {
                      form.setValue('language', v)
                    }}
                    aria-label="Idioma"
                  />
                </AdvSection>

                <AdvSection icon={<Layers />} label="Mídia">
                  <Segmented<MixModeKey>
                    options={MIX_MODES}
                    value={mixMode}
                    onValueChange={(v) => {
                      form.setValue('mix_mode', v)
                    }}
                    aria-label="Mídia"
                  />
                </AdvSection>

                <AdvSection icon={<Music2 />} label="Trilha">
                  <Segmented<MusicMoodKey>
                    options={MUSIC_MOODS}
                    value={musicMood}
                    onValueChange={(v) => {
                      form.setValue('music_mood', v)
                    }}
                    aria-label="Trilha"
                  />
                </AdvSection>

                <AdvSection icon={<TextCursorInput />} label="Legendas">
                  <Segmented<SubtitleKey>
                    options={SUBTITLE_POSITIONS}
                    value={subtitlePosition}
                    onValueChange={(v) => {
                      form.setValue('subtitle_position', v)
                    }}
                    aria-label="Posição das legendas"
                  />
                </AdvSection>

                <AdvSection icon={<DollarSign />} label="Mostrar preço/avaliação">
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <ToggleChip
                      icon={<DollarSign />}
                      label="Preço"
                      active={mentionPrice}
                      onClick={() => {
                        form.setValue('mention_price', !mentionPrice)
                      }}
                    />
                    <ToggleChip
                      icon={<Star />}
                      label="Estrelas"
                      active={mentionRating}
                      onClick={() => {
                        form.setValue('mention_rating', !mentionRating)
                      }}
                    />
                    <ToggleChip
                      icon={<TextCursorInput />}
                      label="Overlays"
                      active={supportTexts}
                      onClick={() => {
                        form.setValue('support_texts', !supportTexts)
                      }}
                    />
                  </div>
                </AdvSection>

                <AdvSection icon={<MessageSquare />} label="CTA personalizado">
                  <input
                    type="text"
                    maxLength={60}
                    placeholder='Ex: "Link na descrição"'
                    className={styles.textarea}
                    {...form.register('custom_cta')}
                  />
                </AdvSection>

                {language === 'both' ? (
                  <>
                    <AdvSection icon={<Mic />} label="Narrador · PT">
                      <VoicePicker
                        pending={voicesPending}
                        options={ptVoices}
                        provider={voiceProvider}
                        value={voicePt ?? ''}
                        onChange={(v) => {
                          form.setValue('tts_voice_pt', v)
                        }}
                      />
                    </AdvSection>
                    <AdvSection icon={<Mic />} label="Narrador · EN">
                      <VoicePicker
                        pending={voicesPending}
                        options={enVoices}
                        provider={voiceProvider}
                        value={voiceEn ?? ''}
                        onChange={(v) => {
                          form.setValue('tts_voice_en', v)
                        }}
                      />
                    </AdvSection>
                  </>
                ) : (
                  <AdvSection icon={<Mic />} label="Narrador">
                    <VoicePicker
                      pending={voicesPending}
                      options={language === 'en' ? enVoices : ptVoices}
                      provider={voiceProvider}
                      value={(language === 'en' ? voiceEn : voicePt) ?? ''}
                      onChange={(v) => {
                        form.setValue(language === 'en' ? 'tts_voice_en' : 'tts_voice_pt', v)
                      }}
                    />
                  </AdvSection>
                )}

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="product_review" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}

interface ToggleChipProps {
  icon: ReactNode
  label: string
  active: boolean
  onClick: () => void
}

function ToggleChip({ icon, label, active, onClick }: ToggleChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(styles.advToggle, active && styles.advToggleOpen)}
      aria-pressed={active}
    >
      <span className={styles.rowIcon} aria-hidden>
        {icon}
      </span>
      {label}
    </button>
  )
}

interface VoicePickerProps {
  pending: boolean
  options: ReturnType<typeof toVoiceOption>[]
  provider: string
  value: string
  onChange: (v: string) => void
}

function VoicePicker({ pending, options, provider, value, onChange }: VoicePickerProps) {
  if (pending) {
    return <span className={styles.voicesLoading}>Carregando vozes…</span>
  }
  if (options.length === 0) {
    return (
      <span className={styles.voicesLoading}>
        Nenhuma voz disponível para o provider “{provider || 'desconhecido'}”.
      </span>
    )
  }
  return <VoiceCarousel options={options} value={value} onValueChange={onChange} />
}
